import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import ListItem from '@mui/material/ListItem';
import List from '@mui/material/List';
import Checkbox from '@mui/material/Checkbox';
import styled from 'styled-components';
import Box from '@mui/material/Box';
import withStyles from '@mui/styles/withStyles';
import { useTranslation } from 'react-i18next';
import theme from 'shared/themes/default';
import T from 'shared/atoms/Typography';
import Pagination from 'shared/molecules/Pagination';
import { newSelectTranslation } from 'shared/utils';
import CircularSpinner from 'shared/atoms/CircularSpinner';
import { usePlaylist } from 'shared/sections/PlaylistAdministration/context';

const StyledThumbnail = styled.img`
  align-self: stretch;
  object-fit: cover;
  margin-right: 6px;
  border-radius: 8px;
  min-width: 70px;
  float: left;
  height: 42px;
  width: 70px;
`;

const StyledTitleName = styled.div`
  text-overflow: ellipsis;
  width: 100%;
  word-break: break-word;
  margin: 0 5px;
  align-self: center;
  font-weight: 600;
  font-size: 14px;
`;

const styles = {
  root: {
    fontFamily: theme.fonts.primary,
    margin: '0',
    display: 'grid',
    paddingLeft: '6px',
    '&:nth-of-type(even)': {
      backgroundColor: theme.palette.lightPure,
    },
    '&:nth-of-type(odd)': {
      backgroundColor: theme.palette.lightInteracting,
    },
    '& .MuiListItem-root': {
      padding: '0 0 0 0',
      overflow: 'hidden',
    },
  },
  pointerCheckbox: {
    cursor: 'pointer',
  },
  defaultCheckbox: {
    cursor: 'not-allowed',
  },
};

const StyledList = styled(List)`
  display: grid;
`;

const StyledOuterWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 330px;
`;

const StyledBox = styled(Box)`
  ::-webkit-scrollbar {
    width: 10px;
    height: 30px;
  }
  ::-webkit-scrollbar-thumb:vertical {
    height: 30px;
    background-color: ${theme.palette.brand};
    border-radius: 10px;
  }
`;

const CourseAndPreviewPanel = ({
  classes,
  coursesSelected,
  coursesToSelect,
  loading,
  initPage,
  onCourseClickHandler,
  searchTerm,
}) => {
  const { locale, staticImagesHostname } = usePlaylist();

  const [page, setPage] = useState(initPage || 1);

  const { t } = useTranslation();

  const handleChange = (event, value) => {
    setPage(value);
  };

  const courses = coursesToSelect.filter((course) =>
    newSelectTranslation(course.name, locale)
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  const itemsPerPage = 5;
  const noOfPages = Math.ceil(courses.length / itemsPerPage || 1);

  useEffect(() => {
    setPage(1);
  }, [courses.length]);

  return (
    <StyledOuterWrapper>
      {loading ? (
        <CircularSpinner />
      ) : (
        <>
          <StyledBox>
            {courses.length === 0 && (
              <T
                align="center"
                color={theme.palette.disabledLight}
                sx={{ marginTop: '25%' }}
                variant="headingXs"
              >
                {t('playlist.noCourseFound')}
              </T>
            )}
            {courses
              .slice((page - 1) * itemsPerPage, page * itemsPerPage)
              .map((course) => {
                const tempCourse = coursesSelected.find(
                  (courseInternal) => courseInternal.id === course.id
                );
                const courseName = newSelectTranslation(course.name, locale);
                return (
                  <StyledList classes={classes} key={course.id}>
                    <ListItem>
                      <Checkbox
                        color="default"
                        aria-label={courseName}
                        onChange={() => onCourseClickHandler(course.id, page)}
                        checked={!!tempCourse}
                      />
                      <StyledThumbnail
                        alt={courseName}
                        src={`${staticImagesHostname}/${course.thumbnail?.file?.key}`}
                      />
                      <StyledTitleName>{courseName}</StyledTitleName>
                    </ListItem>
                  </StyledList>
                );
              })}
          </StyledBox>
          {noOfPages > 1 && (
            <Pagination
              totalPages={noOfPages}
              initialPage={page}
              count={noOfPages}
              page={page}
              onChange={handleChange}
              defaultPage={1}
            />
          )}
        </>
      )}
    </StyledOuterWrapper>
  );
};

CourseAndPreviewPanel.propTypes = {
  classes: PropTypes.object.isRequired,
  coursesSelected: PropTypes.array.isRequired,
  coursesToSelect: PropTypes.array.isRequired,
  loading: PropTypes.bool.isRequired,
  initPage: PropTypes.number,
  onCourseClickHandler: PropTypes.func.isRequired,
  searchTerm: PropTypes.string.isRequired,
};

CourseAndPreviewPanel.defaultProps = {
  initPage: 1,
};

export default withStyles(styles)(CourseAndPreviewPanel);
